'use client';

import styled from '@emotion/styled';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import HostsCard from '@/components/cards/host-card/host-card';
import { fetchContent } from '../../lib/fetch-content';
import { sanityImageUrlFor } from '@/content/sanity/sanity-image-url-for/sanity-image-url-for';
import { config } from '@/config/environment';

const MainDiv = styled.div`
  width: 100%;
  margin: 0;
  max-height: max-content;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #231727;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 98px 20px 0 20px;
  }
`;
const IntroDiv = styled(Grid)`
  width: 100%;
  height: 59vh;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: 540px;
  padding: 15% 12.5%;
  @media (max-width: 768px) {
    padding: 0;
    margin-bottom: 0;
    height: auto;
  }
`;
const MobileBlob = styled.img`
  left: 0;
  top: 400px;
  position: absolute;
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 10.5% 0 12.5%;
  margin: 0 0 100px 0;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: flex-start;
  height: auto;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #fdf3de;
  text-align: center;
  margin: 0;
  @media (max-width: 768px) {
    text-align: start;
    margin-bottom: 32px;
  }
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  text-align: start;
  z-index: 5;
  width: 95%;
  @media (max-width: 768px) {
    margin-bottom: 98px;
    width: 100%;
  }
`;
const Vector = styled(Image)`
  object-fit: cover;
  position: absolute;
  margin-top: 60px;
  left: 0;
  top: -40px;
  width: 37.7%;
  height: auto;
  max-width: 1000px;
  @media (max-width: 420px) {
    display: none;
  }
`;
Vector.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const HostType = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 40px;
  line-height: 112.3%;
  color: #ffffff;
  z-index: 5;
  margin-bottom: 20px;
  width: 100%;
`;
const HostsDiv = styled(Grid)`
  display: flex;
  flex-wrap: wrap;
  height: auto;
  justify-content: flex-start;
`;
const TeamDetailsDiv = styled(Grid)`
  display: flex;
  height: auto;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  ${Text} {
    width: 100%;
    padding-right: 25%;
  }
  @media (max-width: 768px) {
    ${Text} {
      width: 100%;
      padding-right: 0;
    }
  }
`;
const FirstBlob = styled.img`
  object-fit: cover;
  right: 0;
  /* top: 100%; */
  width: 50%;
  height: auto;
  position: absolute;
  @media (max-width: 420px) {
    display: none;
  }
`;
const SecondBlob = styled.img`
  object-fit: cover;
  left: 0;
  width: 50%;
  height: auto;
  /* top: 200%; */
  position: absolute;
  overflow: hidden;
  @media (max-width: 420px) {
    height: 300px;
    width: 300px;
  }
`;
const ThirdBlob = styled.img`
  object-fit: cover;
  right: 0;
  width: 50%;
  height: auto;
  /* top: 300%; */
  overflow: hidden;
  position: absolute;
  @media (max-width: 420px) {
    display: none;
  }
`;
const FourthBlob = styled(Image)`
  left: 0;
  width: 50%;
  height: auto;
  overflow: hidden;
  margin-bottom: -50%;
  position: absolute;
  z-index: 1;
  @media (max-width: 420px) {
    display: none;
  }
`;
FourthBlob.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const TeamMember = styled(Grid)`
  height: 290px;
  width: 250px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 30px;
  margin-right: 20px;
  @media (max-width: 768px) {
    width: 100%;
    height: 408px;
    margin-right: 0;
  }
`;

interface TeamProps {
  content: Awaited<ReturnType<typeof fetchContent>>;
}

export const Team = ({ content }: TeamProps) => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const archiveMode = config.platformMode === 'archive';

  const archiveRoleDescriptions: Record<string, string> = {
    curators:
      'Curators recruited and supported community members to turn their passions and interests into shared experiences. They knocked on doors, organized activities, ran events, brainstormed ideas, built confidence, troubleshot challenges, and gathered feedback to continually improve the community.',
    coaches:
      'Coaches guided and enabled community members to create more of the kinds of moments they wanted in their lives. They helped people set intentions, overcome barriers, and make meaning from their experiences. Skilled at inspiring self-directed action, Coaches listened, asked questions, shared strategies, and celebrated growth.',
    designers:
      "Designers made and tested every part of Curiko—from participatory processes and interactions to materials and this very website. They spent time with people who did and didn't use Curiko to understand their perspectives, uncover new opportunities, and bring ideas to life through ongoing rounds of trial and learning.",
    moderators:
      "Moderators helped facilitate Curiko's online group experiences, working alongside Hosts to create warm, welcoming, and respectful virtual environments. They modelled Curiko's values through curiosity, empathy, and mutual respect, while also supporting all the practical aspects of running a video call.",
    operations:
      'The Operations team helped keep Curiko running behind the scenes. They supported everything from budgeting and staffing to organizing events, coordinating partnerships, and so much more.',
    'social science types':
      'Social science types drew on research, collected stories, and explored how Curiko could enable individual, community, and systems change. Blending sociology, social psychology, anthropology, critical disability studies, and decolonial ways of knowing, they worked to make theory practical and actionable.',
  };

  const getRoleDescription = (roleName: string, fallback: string) => {
    if (!archiveMode) {
      return fallback;
    }

    const normalizedRole = roleName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    return archiveRoleDescriptions[normalizedRole] ?? fallback;
  };

  return (
    <MainDiv>
      <IntroDiv container>
        {!isMobile ? (
          <Vector src="/about-us/our-team/vector.png" alt="" />
        ) : (
          <MobileBlob src="/about-us/our-team/mobile-blob.png" />
        )}
        <TitleDiv item md={6} xs={12}>
          <Title>{archiveMode ? 'The team behind Curiko' : 'Our team'}</Title>
        </TitleDiv>
        <TitleDiv item md={6} xs={12}>
          <Text>
            {archiveMode
              ? 'Behind the Curiko platform was a team of self-advocates, lifelong learners, designers, social scientists, Moderators, Tasters, Curators, and Coaches. We all brought different lived experiences and diverse perspectives to the platform. What tied us together was a shared belief in our collective capacity to grow, learn, and co-create communities where we are all valued and thrive.'
              : content.description}
          </Text>
        </TitleDiv>
      </IntroDiv>
      {content.roles.map((role, index) => (
        <Div container key={role._key}>
          {/* Conditionally render blobs based on role index */}
          {index === 0 && !isMobile && <FirstBlob src="/about-us/our-team/vector1.png" />}
          {index === 2 && !isMobile && <SecondBlob src="/about-us/our-team/vector2.png" />}
          {index === 4 && !isMobile && <ThirdBlob src="/about-us/our-team/vector3.png" />}
          <TeamDetailsDiv item md={6} xs={12}>
            <HostType>{role.role}</HostType>
            <Text>{getRoleDescription(role.role, role.description)}</Text>
          </TeamDetailsDiv>
          <HostsDiv item md={6} xs={12}>
            {role.teamMembers.map(member => (
              <TeamMember key={member._key}>
                <HostsCard
                  member={{
                    name: member.name,
                    pronoun: member.pronoun,
                    info: member.info,
                    pro_pic: sanityImageUrlFor(member.pro_pic).url(),
                  }}
                />
              </TeamMember>
            ))}
            {/* Conditionally render FourthBlob for specific role */}
            {index === 5 && !isMobile && (
              <FourthBlob src="/about-us/our-team/vector5.png" alt="Team Photo" />
            )}
          </HostsDiv>
        </Div>
      ))}
    </MainDiv>
  );
};
